import * as React from "react";
import { useNavigate } from "@tanstack/react-router";
import { Badge, Card, Skeleton } from "@repo/ui";
import {
  Package,
  PackagePlus,
  PackageMinus,
  ArrowLeftRight,
  ExternalLink,
} from "lucide-react";
import { api } from "@/lib/api/client";
import type { ReminderReferenceType } from "@/hooks/use-reminders";

interface EntityReferenceBadgeProps {
  referenceType: ReminderReferenceType;
  referenceId: string;
  referenceName?: string;
  className?: string;
}

interface EntityReferenceCardProps {
  referenceType: ReminderReferenceType;
  referenceId: string;
  referenceName?: string;
  compact?: boolean;
}

const REFERENCE_CONFIG: Record<
  ReminderReferenceType,
  {
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    href: (id: string) => string;
    color: string;
    borderColor: string;
  }
> = {
  inventory: {
    label: "Inventory Item",
    icon: Package,
    href: (id) => `/inventory?highlight=${id}`,
    color: "bg-muted text-foreground",
    borderColor: "border-l-border",
  },
  receipt: {
    label: "Receipt",
    icon: PackagePlus,
    href: (id) => `/receipts?highlight=${id}`,
    color: "bg-muted text-foreground",
    borderColor: "border-l-border",
  },
  dispense: {
    label: "Dispense",
    icon: PackageMinus,
    href: (id) => `/dispenses?highlight=${id}`,
    color: "bg-muted text-foreground",
    borderColor: "border-l-border",
  },
  adjustment: {
    label: "Adjustment",
    icon: ArrowLeftRight,
    href: (id) => `/activity?highlight=${id}`,
    color: "bg-muted text-foreground",
    borderColor: "border-l-border",
  },
};

interface EntityDetails {
  name: string;
  ndc?: string;
  drugType?: string;
  stock?: number;
  status?: string;
  invoiceNumber?: string;
  rxNumber?: string;
  quantity?: number;
  reason?: string;
  date?: string;
}

function formatDrugSchedule(type: string | null | undefined): string {
  if (!type) return "";
  return type.replace("schedule_", "Schedule ").replace("_", " ");
}

function formatStatus(status: string | null | undefined): {
  label: string;
  className: string;
} {
  switch (status) {
    case "in_stock":
      return {
        label: "In Stock",
        className: "bg-emerald-50 text-emerald-700",
      };
    case "reorder_needed":
      return { label: "Reorder", className: "bg-amber-50 text-amber-700" };
    case "sold_out":
      return {
        label: "Out of Stock",
        className: "bg-red-50 text-red-700",
      };
    default:
      return { label: "", className: "" };
  }
}

function useEntityDetails(
  referenceType: ReminderReferenceType,
  referenceId: string
) {
  const [details, setDetails] = React.useState<EntityDetails | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;

    async function fetchDetails() {
      setIsLoading(true);
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let json: any;
        let data: EntityDetails | null = null;

        if (referenceType === "inventory") {
          json = await api.get(`/inventory/${referenceId}`);
          const item = json.data || json;
          data = {
            name: item.item_name || item.name,
            ndc: item.ndc,
            drugType: item.drug_type,
            stock: item.stock_calculated ?? item.stock,
            status: item.status,
          };
        } else if (referenceType === "receipt") {
          json = await api.get(`/receipts/${referenceId}`);
          // API returns { receipt: { ... } }
          const item = json.receipt || json.data || json;
          data = {
            name: item.item_name || item.inventory?.item_name || "Receipt",
            ndc: item.ndc,
            invoiceNumber: item.invoice_number,
            quantity: item.quantity_received,
            date: item.date_received,
          };
        } else if (referenceType === "dispense") {
          json = await api.get(`/dispenses/${referenceId}`);
          // API returns { dispense: { ... } }
          const item = json.dispense || json.data || json;
          data = {
            name: item.item_name || item.inventory?.item_name || "Dispense",
            ndc: item.ndc,
            rxNumber: item.rx_number,
            quantity: item.quantity_dispensed,
            date: item.date,
          };
        } else if (referenceType === "adjustment") {
          json = await api.get(`/inventory/adjustments/${referenceId}`);
          const item = json.adjustment || json.data || json;
          data = {
            name: item.item_name || item.inventory?.item_name || "Adjustment",
            ndc: item.ndc,
            reason: item.reason,
            quantity: item.quantity_adjustment,
            date: item.adjustment_date,
          };
        }

        if (!cancelled && data) {
          setDetails(data);
        }
      } catch {
        // Silently fail - we'll just show the fallback name
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    if (referenceId) {
      fetchDetails();
    }

    return () => {
      cancelled = true;
    };
  }, [referenceType, referenceId]);

  return { details, isLoading };
}

/**
 * Compact badge version for reminder cards
 */
export function EntityReferenceBadge({
  referenceType,
  referenceId,
  referenceName,
  className,
}: EntityReferenceBadgeProps) {
  const navigate = useNavigate();
  const config = REFERENCE_CONFIG[referenceType];
  const Icon = config.icon;
  const { details } = useEntityDetails(referenceType, referenceId);

  const displayName = details?.name || referenceName || config.label;
  const ndc = details?.ndc;

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const href =
      referenceType === "inventory" && ndc
        ? `/inventory?search=${encodeURIComponent(ndc)}`
        : config.href(referenceId);
    navigate({ to: href });
  };

  return (
    <Badge
      variant="outline"
      className={`cursor-pointer gap-1 text-xs h-5 px-1.5 hover:bg-muted/50 transition-colors ${className || ""}`}
      onClick={handleClick}
    >
      <Icon className="h-3 w-3 text-primary" />
      <span className="truncate max-w-[180px]">{displayName}</span>
    </Badge>
  );
}

/**
 * Rich card version for the create dialog and detail panel
 */
export function EntityReferenceCard({
  referenceType,
  referenceId,
  referenceName,
  compact = false,
}: EntityReferenceCardProps) {
  const navigate = useNavigate();
  const config = REFERENCE_CONFIG[referenceType];
  const Icon = config.icon;
  const { details, isLoading } = useEntityDetails(referenceType, referenceId);

  const handleNavigate = () => {
    const href =
      referenceType === "inventory" && details?.ndc
        ? `/inventory?search=${encodeURIComponent(details.ndc)}`
        : config.href(referenceId);
    navigate({ to: href });
  };

  if (isLoading) {
    return (
      <div className="rounded-lg border p-3 space-y-2">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-3 w-48" />
      </div>
    );
  }

  const displayName = details?.name || referenceName || config.label;

  return (
    <Card
      className={`cursor-pointer transition-all hover:shadow-sm border-l-4 ${config.borderColor}`}
      onClick={handleNavigate}
    >
      <div className={compact ? "p-3" : "p-4"}>
        {/* Header: type badge + navigate icon */}
        <div className="flex items-center justify-between mb-2">
          <div
            className={`inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-xs font-medium ${config.color}`}
          >
            <Icon className="h-3 w-3" />
            {config.label}
          </div>
          <ExternalLink className="h-3.5 w-3.5 text-muted-foreground" />
        </div>

        {/* Drug / item name */}
        <p className={`font-medium leading-tight ${compact ? "text-sm" : ""}`}>
          {displayName}
        </p>

        {/* Details row */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5">
          {details?.ndc && (
            <span className="text-xs text-muted-foreground font-mono">
              NDC: {details.ndc}
            </span>
          )}
          {details?.drugType && (
            <span className="text-xs text-muted-foreground">
              {formatDrugSchedule(details.drugType)}
            </span>
          )}
          {details?.stock !== undefined && details?.stock !== null && (
            <span className="text-xs font-medium">Stock: {details.stock}</span>
          )}
          {details?.status && (
            <span
              className={`text-[10px] font-medium rounded px-1.5 py-0.5 ${formatStatus(details.status).className}`}
            >
              {formatStatus(details.status).label}
            </span>
          )}
          {details?.invoiceNumber && (
            <span className="text-xs text-muted-foreground font-mono">
              Invoice: {details.invoiceNumber}
            </span>
          )}
          {details?.rxNumber && (
            <span className="text-xs text-muted-foreground font-mono">
              RX: {details.rxNumber}
            </span>
          )}
          {details?.quantity !== undefined && (
            <span className="text-xs font-medium">Qty: {details.quantity}</span>
          )}
          {details?.reason && (
            <span className="text-xs text-muted-foreground capitalize">
              {details.reason.replace(/_/g, " ")}
            </span>
          )}
        </div>
      </div>
    </Card>
  );
}
